/**
 * Kamerazugriff.
 *
 * Die Fehlerfälle sind wichtiger als der Normalfall: eine Duty-Kraft am
 * Eingang braucht einen Satz, den sie versteht und der ihr sagt, was zu tun
 * ist. Nicht „NotAllowedError".
 */
export type CameraError =
  | 'insecure-context'
  | 'unsupported'
  | 'permission-denied'
  | 'not-found'
  | 'in-use'
  | 'unknown';

export const CAMERA_MESSAGE: Record<CameraError, string> = {
  'insecure-context':
    'Die Kamera braucht eine gesicherte Verbindung (https). Bitte die richtige Adresse öffnen.',
  unsupported:
    'Dieses Gerät oder dieser Browser gibt die Kamera nicht frei. Bitte die Jamaat-ID eintippen.',
  'permission-denied':
    'Der Kamerazugriff wurde abgelehnt. In den Browsereinstellungen erlauben oder die Jamaat-ID eintippen.',
  'not-found': 'Es wurde keine Kamera gefunden. Bitte die Jamaat-ID eintippen.',
  'in-use':
    'Die Kamera wird von einer anderen App benutzt. Bitte diese schliessen oder die Jamaat-ID eintippen.',
  unknown: 'Die Kamera lässt sich nicht öffnen. Bitte die Jamaat-ID eintippen.',
};

export function classifyCameraError(error: unknown): CameraError {
  const name = (error as { name?: string } | undefined)?.name ?? '';
  if (name === 'NotAllowedError' || name === 'SecurityError') return 'permission-denied';
  if (name === 'NotFoundError' || name === 'OverconstrainedError') return 'not-found';
  if (name === 'NotReadableError' || name === 'AbortError') return 'in-use';
  return 'unknown';
}

export interface CameraHandle {
  stream: MediaStream;
  stop(): void;
}

export async function openCamera(
  nav: Pick<Navigator, 'mediaDevices'> = navigator,
  secure = typeof window === 'undefined' ? true : window.isSecureContext,
): Promise<CameraHandle> {
  // getUserMedia ist ohne gesicherten Kontext nicht verfügbar. Die Meldung
  // muss das sagen, sonst sucht jemand am Eingang nach der Berechtigung.
  if (!secure) throw Object.assign(new Error('insecure'), { cameraError: 'insecure-context' });
  if (!nav.mediaDevices?.getUserMedia) {
    throw Object.assign(new Error('unsupported'), { cameraError: 'unsupported' });
  }

  const stream = await nav.mediaDevices.getUserMedia({
    video: {
      // Rückkamera: die Karte wird vom Gerät weg gehalten.
      facingMode: 'environment',
      // Ein Strichcode braucht Bildpunkte quer über die Striche. Bei 1280
      // blieben davon zu wenige übrig, sobald die Karte nicht das halbe
      // Bild füllt. `ideal`, nicht `exact`: eine schwächere Kamera liefert
      // dann eben weniger, statt gar nichts.
      width: { ideal: 1920 },
      height: { ideal: 1080 },
    },
    audio: false,
  });

  return {
    stream,
    stop: () => stream.getTracks().forEach((track) => track.stop()),
  };
}
